import { Component, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { EditorService } from '../../infrastructure/editor.service';
import { DownloadService } from './download.service';
import { MatIconModule } from '@angular/material/icon';
import {
  type AnalyticsErrorCategory,
  AnalyticsService,
  ButtonDirective,
  LOCAL_IMAGE_PROCESSING_ENABLED,
  SpinnerComponent,
} from '@imagise/shared';
import { TranslateModule } from '@ngx-translate/core';
import {
  EditService,
  type EditImageRequest as GeneratedEditImageRequest,
} from '@imagise/generated-imagise-cloud-api-client';
import { ErrorReportOperation, ErrorResponse, FileResult, serializeEditImageRequest } from '@imagise/entities';
import { defer } from 'rxjs';
import { processImage, supportsImageRequest, type ImageRequest } from '@imagise/image-processing';
import {
  buildEditImageRequestFromState,
  buildLocalResizePlan,
  buildOutputFileName,
  type EditorCommittedEdit,
  type EditorFormValue,
  EditorFormStore,
  normalizeDestinationFormat,
  resolveOutputExtension,
  toCommittedEdit,
  UnsupportedEditorEdit,
} from '../../domain';
import { getEditorAnalyticsErrorCategory } from '../../domain/editor-analytics';
import { ErrorReportingService } from '../../../error-reporting/error-reporting.service';
import { captureOperationRequest, checkOperationResponse } from '../../../error-reporting/operation-request';

export interface DownloadDialogData {
  toolSlug: string;
  uploadedFileName: string;
  sourceFile: FileResult;
  editorStore: EditorFormStore;
}

interface FormatOption {
  Name: string;
}

const FORMAT_ABBREVIATION_LENGTH = 5;
// The backend can only assemble animations into these containers
// (PNG is transparently converted to APNG).
const ANIMATION_SAVE_AS_FORMATS: string[] = ['GIF', 'WEBP', 'APNG', 'PNG'];
const DOWNLOAD_DRAFT_EDIT_ID = '__download-draft__';
const SUPPORTED_SAVE_AS_FORMATS: string[] = [
  'PDF',
  'PSD',
  'JPG',
  'JP2',
  'J2K',
  'GIF',
  'PNG',
  'APNG',
  'BMP',
  'TIFF',
  'TGA',
  'WEBP',
  'DICOM',
  'SVG',
  'EMF',
  'EMZ',
  'WMF',
  'WMZ',
  'ICO',
];

@Component({
  selector: 'lib-download-dialog',
  imports: [ButtonDirective, MatIconModule, SpinnerComponent, TranslateModule],
  templateUrl: './download-dialog.component.html',
  styleUrl: './download-dialog.component.scss',
})
export class DownloadDialogComponent {
  readonly data = inject<DownloadDialogData>(MAT_DIALOG_DATA);
  readonly dialogRef = inject(MatDialogRef<DownloadDialogComponent>);
  readonly editorService = inject(EditorService);
  readonly #editService = inject(EditService);
  readonly downloadService = inject(DownloadService);
  readonly #analytics = inject(AnalyticsService);
  readonly #localImageProcessingEnabled = inject(LOCAL_IMAGE_PROCESSING_ENABLED);
  readonly #errorReporting = inject(ErrorReportingService);

  public downloadLink = signal<string | null>(null);
  public downloadFileName = signal<string | null>(null);
  public errorMessage = signal<string | null>(null);

  formats = (this.hasAnimation() ? [...ANIMATION_SAVE_AS_FORMATS] : [...SUPPORTED_SAVE_AS_FORMATS]).sort((a, b) =>
    a.localeCompare(b)
  );

  _selectedFormat: FormatOption = { Name: this.hasAnimation() ? 'GIF' : '' };

  resultFile: FileResult | null = null;

  editorProcess = signal(false);

  /** Output format frozen for the in-flight download request. */
  private processingFormat: string | null = null;
  private processingOperation: ErrorReportOperation | null = null;

  /** Output format belonging to the currently rendered download link. */
  private downloadOutputFormat: string | null = null;

  /** True when the result will be an animation — limits the format choice. */
  private hasAnimation(): boolean {
    const snapshot = this.data.editorStore.snapshot();

    if (snapshot.draft?.kind === 'animation' && snapshot.draft.params.frames.length > 1) {
      return true;
    }

    return snapshot.committedEdits.slice(0, snapshot.history.cursor).some(edit => edit.kind === 'animation');
  }

  public static open(dialog: MatDialog, data: DownloadDialogData): MatDialogRef<DownloadDialogComponent> {
    return dialog.open(DownloadDialogComponent, {
      data,
      width: '80vw',
      panelClass: 'dark-theme-dialog',
      backdropClass: 'dark-theme-dialog-backdrop',
    });
  }

  getFormatAbbreviation(format: string): string {
    if (format === 'HTML5 Canvas') {
      return 'HTML';
    }

    return format.substring(0, FORMAT_ABBREVIATION_LENGTH);
  }

  formatColor(index: number): string {
    const hue = (index * 360) / this.formats.length;
    return `hsl(${hue} 70% 45%)`;
  }

  selectFormat(format: string): void {
    if (this.editorProcess() || !this.formats.includes(format)) {
      return;
    }

    this._selectedFormat.Name = format;
    this.errorMessage.set(null);
    this.data.editorStore.setOutputFormat(normalizeDestinationFormat(format));
  }

  onGetResult(): void {
    if (this.editorProcess() || !this.formats.includes(this._selectedFormat.Name)) {
      return;
    }

    const outputFormat = this._selectedFormat.Name;
    this.processingFormat = outputFormat;
    this.editorProcess.set(true);
    this.errorMessage.set(null);
    this.#analytics.track('processing_start', {
      tool_slug: this.data.toolSlug,
      operation: 'export',
      output_format: resolveOutputExtension(outputFormat),
    });
    this.requestResult();
  }

  private requestResult(): void {
    const outputFormat = this.processingFormat;
    if (!outputFormat) {
      return;
    }

    const editorSnapshot = this.data.editorStore.snapshot();
    const appliedEdits = editorSnapshot.committedEdits.slice(0, editorSnapshot.history.cursor);
    const draftEdit = editorSnapshot.draft ? toCommittedEdit(DOWNLOAD_DRAFT_EDIT_ID, editorSnapshot.draft) : null;

    if (appliedEdits.length === 0 && !draftEdit && !editorSnapshot.source.sourceBase64) {
      this.applyFormatConversionFallback(outputFormat);
      return;
    }

    if (this.#localImageProcessingEnabled) {
      const localResizePlan = buildLocalResizePlan(
        editorSnapshot.source,
        draftEdit ? [...appliedEdits, draftEdit] : appliedEdits,
        outputFormat
      );
      const sourceBytes = localResizePlan ? this.readLocalSourceBytes() : null;

      if (localResizePlan && sourceBytes) {
        void this.requestLocalResult(editorSnapshot, draftEdit, localResizePlan, sourceBytes);
        return;
      }
    }

    this.requestBackendResult(editorSnapshot, draftEdit);
  }

  private async requestLocalResult(
    editorSnapshot: EditorFormValue,
    draftEdit: EditorCommittedEdit | null,
    plan: Omit<ImageRequest, 'bytes'>,
    sourceBytes: Promise<ArrayBuffer>
  ): Promise<void> {
    try {
      const request: ImageRequest = { ...plan, bytes: await sourceBytes };
      if (!supportsImageRequest(request)) {
        this.requestBackendResult(editorSnapshot, draftEdit);
        return;
      }

      this.processingOperation = {
        operation: 'local-export', parameters: { request: JSON.stringify(plan) },
        files: [{ name: editorSnapshot.source.fileName, blob: new Blob([request.bytes]) }],
      };
      const result = await processImage(request);
      this.onEditedBlobReady(new Blob([result.bytes], { type: result.format }));
    } catch {
      this.requestBackendResult(editorSnapshot, draftEdit);
    }
  }

  private readLocalSourceBytes(): Promise<ArrayBuffer> | null {
    if (this.data.sourceFile.FileBlob) {
      return this.data.sourceFile.FileBlob.arrayBuffer();
    }
    if (this.data.sourceFile.LocalObjectUrl) {
      return fetch(this.data.sourceFile.LocalObjectUrl).then(response => response.arrayBuffer());
    }
    return null;
  }

  private requestBackendResult(editorSnapshot: EditorFormValue, draftEdit: EditorCommittedEdit | null): void {
    const { request, unsupportedEdits } = buildEditImageRequestFromState(editorSnapshot, {
      additionalEdits: draftEdit ? [draftEdit] : [],
    });

    if (unsupportedEdits.length > 0) {
      this.errorMessage.set(this.buildUnsupportedMessage(unsupportedEdits));
      this.finishProcessingError('unsupported');
      return;
    }

    const wireRequest = serializeEditImageRequest(request);
    const appliedEdits = editorSnapshot.committedEdits.slice(0, editorSnapshot.history.cursor);
    const imageNames = [editorSnapshot.source.fileName,
      ...[...appliedEdits, ...(draftEdit ? [draftEdit] : [])].flatMap(edit =>
        edit.kind === 'animation' ? edit.params.frames.slice(1).map(frame => frame.name) : []),
    ];
    this.processingOperation = captureOperationRequest('export', '/edit', wireRequest, imageNames);
    this.#editService.edit(wireRequest as GeneratedEditImageRequest).pipe(checkOperationResponse()).subscribe({
      next: resultBlob => this.onEditedBlobReady(resultBlob),
      error: error => this.handleProcessingError(error),
    });
  }

  removeLink(): void {
    this.downloadLink.set(null);
    this.downloadFileName.set(null);
    this.downloadOutputFormat = null;
  }

  onDownload(): void {
    if (this.downloadOutputFormat) {
      this.#analytics.track('file_download', {
        tool_slug: this.data.toolSlug,
        output_format: this.downloadOutputFormat,
      });
    }
    this.removeLink();
  }

  private applyFormatConversionFallback(outputFormat: string): void {
    this.processingOperation = captureOperationRequest('convert', '/convert', {
      toFormat: normalizeDestinationFormat(outputFormat),
      imageBase64Files: this.data.sourceFile.SourceBase64 ? [this.data.sourceFile.SourceBase64] : [],
    }, [this.data.sourceFile.FileName]);
    defer(() => this.editorService
      .applyEditorChanges([], outputFormat, this.data.uploadedFileName, this.data.sourceFile))
      .subscribe({
        next: operationStatus => {
          if (operationStatus.State !== 'Completed') {
            this.handleProcessingError(new Error('Image conversion did not complete.'));
            return;
          }

          const processedFile = operationStatus.Payload;
          this.downloadService.requestDownloadLink(processedFile).subscribe({
            next: downloadFile => this.finishDownload(downloadFile),
            error: error => this.handleProcessingError(error),
          });
        },
        error: error => this.handleProcessingError(error),
      });
  }

  private onEditedBlobReady(resultBlob: Blob): void {
    const processedFile = this.createProcessedFile(resultBlob);

    this.downloadService.requestDownloadLink(processedFile).subscribe({
      next: downloadFile => this.finishDownload(downloadFile),
      error: error => this.handleProcessingError(error),
    });
  }

  private finishDownload(downloadFile: FileResult): void {
    const outputFormat = this.processingFormat;
    if (!outputFormat) {
      return;
    }

    this.resultFile = downloadFile;
    const analyticsOutputFormat = resolveOutputExtension(outputFormat);
    this.downloadOutputFormat = analyticsOutputFormat;
    this.clearEditorProcess();
    this.downloadLink.set(this.downloadService.getDownloadLink(downloadFile));
    this.downloadFileName.set(downloadFile.FileName);
    this.#analytics.track('processing_success', {
      tool_slug: this.data.toolSlug,
      operation: 'export',
      output_format: analyticsOutputFormat,
    });
  }

  private handleProcessingError(error: unknown): void {
    const xhr = error as ErrorResponse;
    if (this.processingOperation) this.#errorReporting.reportOperationError(error, this.processingOperation);
    this.finishProcessingError(getEditorAnalyticsErrorCategory(error));
    if (xhr.status) {
      console.error(error);
    }
  }

  private finishProcessingError(errorCategory: AnalyticsErrorCategory): void {
    const outputFormat = this.processingFormat;
    if (!outputFormat) {
      return;
    }

    this.clearEditorProcess();
    this.#analytics.track('processing_error', {
      tool_slug: this.data.toolSlug,
      operation: 'export',
      output_format: resolveOutputExtension(outputFormat),
      error_category: errorCategory,
    });
  }

  private clearEditorProcess(): void {
    this.editorProcess.set(false);
    this.processingFormat = null;
    this.processingOperation = null;
  }

  private createProcessedFile(resultBlob: Blob): FileResult {
    const outputFormat = this.processingFormat ?? this._selectedFormat.Name;

    return {
      IsSuccess: true,
      idError: '',
      id: this.data.sourceFile.id,
      FileName: buildOutputFileName(this.data.uploadedFileName, outputFormat),
      UploadFileName: '',
      idUpload: this.data.sourceFile.idUpload,
      FileLength: resultBlob.size,
      SourceBase64: this.data.sourceFile.SourceBase64,
      SourceMimeType: resultBlob.type || this.data.sourceFile.SourceMimeType,
      LocalObjectUrl: this.data.sourceFile.LocalObjectUrl,
      FileBlob: resultBlob,
    };
  }

  private buildUnsupportedMessage(unsupportedEdits: UnsupportedEditorEdit[]): string {
    const uniqueReasons = Array.from(new Set(unsupportedEdits.map(edit => edit.reason)));
    return uniqueReasons.join(' ');
  }
}
